import React, { useMemo } from "react";

import { MetricsDashboardGraphDimension, MetricsDashboardGraphDimensionFormat, MetricsDashboardGraphTypeDisplayType } from "../../../__generated__/api";
import ReportFieldSelector from "../../../components/pickers/field-selector/ReportFieldSelector";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import { Themes, useTheme } from "../../../contexts/Theme";
import Styles from "../Reports.module.scss";
import {
    CHART_COLORS,
    createDisplayFormatter,
    filter, formatDate, formatValueWithDisplay, getMetricValue, getSortValue, GraphProps,
    GraphSettingsProperties,
    isDate,
    isDimension,
    isNumeric,
} from "./base";
import Chart from "./Chart";
import ColumnProperties from "./Column";
import Filters from "./Filters";
import Title from "./Title";
import createId from "../../../create-id";
import FormatOptions from "./FormatOptions";

export const COLORS_DARK = [
    "#768CFF",
    "#2842C7",
    "#FF833D",
    "#2D9D60",
    "#BBE3FA",
    "#B6C1FF",
    "#82D398",
    "#3FABE5",
    "#FEA97A",
    "#A1AFC0",
];

export const COLORS_LIGHT = [
    "#4E6AFF",
    "#102071",
    "#FF833D",
    "#3ABA6D",
    "#BBE3FA",
    "#B6C1FF",
    "#82D398",
    "#3FABE5",
    "#FEA97A",
    "#C8D2DE",
];

interface TooltipParams {
    name: string;
    seriesName: string;
    percent: number;
    value: number;
    color: string;
    seriesIndex: number;
}

export function LineChartProperties(props: GraphSettingsProperties) {
    const dimension = props.graph.dimensions?.firstElement;
    return (

        <div>

            <FormGroup
                label="Dimension"
                required
                size="small"
                helpDisplay="icon"
                helpText="What value do you want displayed in the line graph"
            >
                <ReportFieldSelector
                    value={dimension?.fieldId}
                    dataSource={props.graph.dataSource}
                    onChange={value => {
                        const dimensionCopy = { ...dimension };
                        dimensionCopy.fieldId = value?.field.id;
                        if (isDate(value?.field.type)) {
                            dimensionCopy.format = MetricsDashboardGraphDimensionFormat.YearMonthDayHourMinute;
                        } else {
                            delete dimensionCopy.format;
                        }
                        props.onChange({
                            ...props.graph,
                            dimensions: [dimensionCopy as MetricsDashboardGraphDimension],
                        });
                    }}
                />
            </FormGroup>

            {isDimension(dimension) && dimension.format && (
                <FormGroup
                    label="Format"
                    size="small"
                >
                    <FormatOptions
                        value={dimension.format as MetricsDashboardGraphDimensionFormat}
                        onChange={format => {
                            const dimensionCopy = { ...dimension };
                            dimensionCopy.format = format;
                            props.onChange({
                                ...props.graph,
                                dimensions: [dimensionCopy as MetricsDashboardGraphDimension],
                            });
                        }}
                    />
                </FormGroup>
            )}

            <ColumnProperties
                field="metrics"
                label="Metrics"
                helpText="The list of items that will be used for the values of the line chart."
                canSetAggregation
                // canAddLabel
                canAddFormat
                {...props}
            />

            <Filters isAdmin={props.isAdmin} graph={props.graph} onChange={props.onChange} />

        </div>
    );
}

export default function LineChart(props: GraphProps) {
    const theme = useTheme();
    const options = useMemo(() => {
        const dimension = props.graph.dimensions?.firstElement;
        if (!dimension) {
            return {};
        }
        const dimensionIndex = props.columnIndexMap[dimension.fieldId];

        const dataRows = filter(props.data.rows, props.graph.filters, props.columnIndexMap);

        const rows = dataRows.groupBy(row => {
            let value = row.values[dimensionIndex] as string;
            const date = new Date(value);
            if (dimension.format && !Number.isNaN(date)) {
                value = formatDate(date, dimension.format);
            }
            return value;
        });

        const keys = Object.keys(rows);
        const isNumberField = isNumeric(props.data.columns[dimensionIndex]?.field.type);
        const xAxis = dimension.format
            ? keys.sortBy(key => getSortValue(
                new Date(rows[key].firstElement.values[dimensionIndex] as string),
                dimension.format as MetricsDashboardGraphDimensionFormat,
            ))
            : keys.sortBy(key => (isNumberField ? parseInt(key, 10) : key));
        const xAxisNames = xAxis.map(key => props.labelMap[key] || key);

        const colors = theme.getTheme() === Themes.DARK ? COLORS_DARK : COLORS_LIGHT;

        const metricDisplay = props.graph.metrics?.firstElement?.display;
        const valueData = props.graph.metrics?.map((metric, metricIndexValue) => {
            const metricIndex = props.columnIndexMap[metric.fieldId];

            const data = xAxis.map(key => {
                const keyRows = rows[key];
                const keyRowData = keyRows
                    .map(row => row.values[metricIndex])
                    .filter(row => row != null && row !== "") as string[];
                return getMetricValue(keyRowData, metric.aggregation) ?? 0;
            });

            return {
                type: "line",
                name: metric.label || props.data.columns[metricIndex]?.field.name || metric.fieldId,
                data,
                label: {
                    show: false,
                },
                color: colors[metricIndexValue % colors.length],
            };
        });

        return {
            tooltip: {
                trigger: "item",
                padding: 0,
                borderWidth: 0,
                formatter: (params: TooltipParams) => {
                    const display = props.graph.metrics?.[params.seriesIndex].display;
                    const value = formatValueWithDisplay(params.value, display ?? MetricsDashboardGraphTypeDisplayType.Number);

                    const className = `color-${createId().substring(0, 10)}`;

                    return `
                        <style nonce=${window.NONCE}>
                            .${className} {
                                background-color: ${params.color}
                            }
                        </style>
                        <div class=${Styles["time-tooltip"]}>
                            <strong class=${Styles["time-tooltip-title"]}>${params.seriesName}</strong>
                            <div class=${Styles["time-tooltip-row"]}>
                                <div class="${`${className} ${Styles["time-circle"]}`}"></div>
                                <div class=${Styles["time-tooltip-row-title"]}>
                                    ${params.name}
                                </div>
                                <div>${value}</div>
                            </div>
                        </div>
                    `;
                },
            },
            grid: {
                left: "20px",
                right: "20px",
                bottom: "10px",
                top: "20px",
                containLabel: true,
            },

            testId: props.testId,
            xAxis: {
                type: "category",
                data: xAxisNames,
                axisTick: {
                    alignWithLabel: true,
                },
                axisLabel: {
                    color: CHART_COLORS.text(),
                    margin: 15,
                    padding: 0,
                    width: "90",
                    overflow: "truncate",
                    rotate: 30,
                },

                axisLine: {
                    show: true,
                },

            },

            yAxis: {
                type: "value",
                axisLabel: {
                    color: CHART_COLORS.text(),
                    margin: 5,
                    padding: 0,
                    formatter: metricDisplay ? createDisplayFormatter(metricDisplay) : undefined,
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: "dashed",
                        color: CHART_COLORS.grid(),
                    },
                },
            },
            series: valueData,
        };
    }, [props.data, props.graph]);

    return (
        <div className={Styles["line-chart"]}>
            <Title isAdmin={props.isAdmin} graph={props.graph} createMenu={props.createMenu} />

            <Chart options={options} ref={ref => ref?.rerender()} />
        </div>
    );
}
